import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import { isEngagement, isRole } from "PFApp/activities/helpers";
import { BookingCategorySelect } from "PFApp/booking/components/booking_category_select";
import { useAvailableBookingCategories } from "PFApp/booking/components/booking_category_select/use_available_booking_categories";
import { getGrowlId, useGrowlStoreActions } from "PFApp/stores/growl_store";
import { SelectProps } from "PFComponents/select";
import { isActivityFieldPermitted } from "PFCore/helpers/activity_fields_permissions";
import { stringify } from "PFCore/helpers/use_deterministic_stringify";
import { useCurrentProfile } from "PFCore/hooks/queries/profile";
import useBookingCategories from "PFCore/hooks/use_booking_categories";
import {
  AccessLevel,
  Activity,
  BookingCategory as BookingCategoryType,
  Id,
  Profile,
  Subtemplate,
  Template
} from "PFCore/types";
import { CSSProperties, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";

type BookingCategorySelectorProps = {
  activityCreator: Profile | null;
  onChange: (bookingCategoryId: Id | null) => void;
  bookingCategoryId?: Id | null;
  template: Template;
  error?: string;
  subtemplate: Subtemplate | null;
  parentActivity?: Activity;
  onRestore?: () => void;
  required?: boolean;
  label?: string;
  title?: string;
  variant?: SelectProps["variant"];
  portalRef?: React.RefObject<HTMLDivElement | null>;
  dropdownWidth?: number;
  disabled?: boolean;
  customInvalidValueMessage?: string;
  tabIndex?: number;
  style?: CSSProperties;
  focusSearchInputOnOpen?: boolean;
};

export const BookingCategorySelector = ({
  activityCreator = null,
  onChange,
  bookingCategoryId,
  template,
  subtemplate,
  parentActivity,
  error,
  onRestore,
  required,
  label,
  title,
  variant,
  portalRef,
  dropdownWidth,
  disabled,
  customInvalidValueMessage,
  tabIndex,
  style,
  focusSearchInputOnOpen
}: BookingCategorySelectorProps) => {
  const { data: currentProfile } = useCurrentProfile();
  const { t } = useTranslation("activities", { keyPrefix: "form" });
  const { t: translationT } = useTranslation("translation");
  const { showGrowl } = useGrowlStoreActions();
  const { getBookingCategory } = useBookingCategories();

  const isRoleTemplate = isRole(template);
  const isEngagementTemplate = isEngagement(template);

  const templateHasSubTemplates = !isEmpty(template.schema?.subtemplates);
  const isBookingCategorySelectDisabled =
    (isEngagementTemplate || isRoleTemplate) && templateHasSubTemplates && !subtemplate;

  const isPermittedToWriteBookingCategoryField = isActivityFieldPermitted({
    currentProfile,
    activityCreator,
    fieldName: "booking_category",
    accessLevel: AccessLevel.ReadWrite
  });

  const getAvailableBookingCategories = useAvailableBookingCategories();
  const availableBookingCategoryValues = getAvailableBookingCategories({
    activityData: [
      {
        activityTemplateId: template.id,
        activitySubtemplateKey: subtemplate?.subtemplate_key,
        parentActivitySubtemplateKey: parentActivity?.subtemplate_key
      }
    ]
  });

  const isCategoryValid = useCallback(
    (category: BookingCategoryType) => {
      if (!isEngagementTemplate && !isRoleTemplate) {
        return true;
      }

      return availableBookingCategoryValues?.includes(category.id);
    },
    [isEngagementTemplate, isRoleTemplate, availableBookingCategoryValues]
  );

  useEffect(() => {
    const bookingCategory = getBookingCategory(bookingCategoryId);
    if (!isNil(bookingCategory) && !isCategoryValid(bookingCategory)) {
      onChange(null);
      showGrowl({
        message:
          customInvalidValueMessage ?? t("sections.basicInformation.bookingCategory.bookingCategoryNotValid"),
        kind: "alert",
        hidePreviousByTypeAndId: true,
        id: getGrowlId("booking-category-not-valid", "alert")
      });
    }
  }, [stringify(availableBookingCategoryValues), bookingCategoryId]);

  return (
    <BookingCategorySelect
      disabled={disabled || !isPermittedToWriteBookingCategoryField || isBookingCategorySelectDisabled}
      onCategoryChange={(bookingCategory) => onChange(bookingCategory?.id || null)}
      category={getBookingCategory(bookingCategoryId) ?? null}
      withoutLabel={!label}
      label={label}
      title={title}
      error={error}
      demand
      letClear
      onRestore={onRestore}
      required={required}
      filterBookingCategory={isCategoryValid}
      locked={!isPermittedToWriteBookingCategoryField}
      lockedTip={
        !isPermittedToWriteBookingCategoryField
          ? translationT("customValues.tips.noPermissionsTip")
          : undefined
      }
      variant={variant}
      portalRef={portalRef}
      dropdownWidth={dropdownWidth}
      focusSearchInputOnOpen={focusSearchInputOnOpen}
      tabIndex={tabIndex}
      style={style}
    />
  );
};
